import type { Logger } from "@expert/logging";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useDisclosure } from "@mantine/hooks";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useMemo } from "react";
import { automaticallySelectGuideIfNeeded } from "../logic-builder";
import { getGuideAnalyticData, useGuideStore } from "../store";
import type { CloseGuideSource, OpenGuideSource } from "../types";

interface GuideDisclosureProps {
    callSid?: string;
    logger: Logger;
    platformTicketId: string;
}

export interface EndedSessionProps {
    endedSessionId: string;
    endedSessionCallSid?: string;
}

export const useGuideDisclosure = ({ callSid, logger: loggerProp, platformTicketId }: GuideDisclosureProps) => {
    const logger = useMemo(
        () => loggerProp.child({ module: "useGuideDisclosure", platformTicketId }),
        [loggerProp, platformTicketId],
    );

    const { dispatcher } = useReactAnalytics();
    const enhancedDispatcher = callSid ? dispatcher.withIdentities({ CallSid: callSid }) : dispatcher;

    const { analyticsInfo, availableGuides, setCurrentGuideName, setDisableAutomaticOpen } = useGuideStore();

    const [isGuideOpen, { close, open }] = useDisclosure(false);

    const openGuide = (source: OpenGuideSource, guide?: string) => {
        logger.info(
            `Solve Guide | openGuide called with source: ${source}, guide: ${guide}, on step: ${analyticsInfo.currentStepId}`,
        );

        // TODO: fix flicker [EWS-1306](https://solutonashville.atlassian.net/browse/EWS-1306)
        open();

        if (["auto", "recommendation"].includes(source) && guide) {
            automaticallySelectGuideIfNeeded(analyticsInfo, availableGuides, guide, logger, setCurrentGuideName);
        } else if (source === "toolbox" && availableGuides.length === 1) {
            // if only one guide exists, skip GuideSelection screen and go to first step of available guide
            automaticallySelectGuideIfNeeded(
                analyticsInfo,
                availableGuides,
                availableGuides[0].title,
                logger,
                setCurrentGuideName,
            );
        }

        const extra = getGuideAnalyticData();
        const openedGuide = guide || extra.guide;

        logger.info({ ...extra, source }, "Solve Guide | launch");
        /*
            Disclaimer, the difference between LaunchGuide and OpenGuide events:
            Both trigger anytime the guide card componenent itself is opened, however...
            - OpenGuide does not trigger if the guide is opened and is on the GuideSelection screen
            (i.e. when a guide hasn't been selected yet)
        */
        void enhancedDispatcher.dispatchBusinessEvent("Guide_LaunchGuide", {
            source,
            ...extra,
            guide: openedGuide,
        });
        trackFullStoryEvent("Guide_LaunchGuide", {
            source,
            ...extra,
            guide: openedGuide,
        });

        // This will be false when the guide card is launched from the toolbox and no guide is selected yet.
        if (openedGuide) {
            logger.info({ ...extra, source }, "Solve Guide | open");
            void enhancedDispatcher.dispatchBusinessEvent("Guide_OpenGuide", {
                source,
                ...extra,
                guide: openedGuide,
            });
        }

        setDisableAutomaticOpen(false);
    };

    const closeGuide = (source: CloseGuideSource, endedSession?: EndedSessionProps) => {
        close();

        /*
            When guide auto-closes at the end of a call, the dispatcher has already
            updated to the new session info, extraEnhancedDispatcher ensures the
            CloseGuide event is sent with previous session info.
        */
        const extraEnhancedDispatcher = endedSession
            ? enhancedDispatcher.withIdentities({
                  SessionId: endedSession.endedSessionId,
                  CallSid: endedSession.endedSessionCallSid,
              })
            : enhancedDispatcher;

        const extra = { ...getGuideAnalyticData(), source };
        logger.info({ ...extra, source }, "Solve Guide | close");

        void extraEnhancedDispatcher.dispatchBusinessEvent("Guide_CloseGuide", {
            ...extra,
        });
        trackFullStoryEvent("Guide_CloseGuide", {
            ...extra,
        });
    };

    return {
        isGuideOpen,
        openGuide,
        closeGuide,
    };
};
